import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Breadcrumb, Button, Form, Input, Select, Upload, message } from "antd";
import { UploadOutlined } from "@ant-design/icons";
import {
  api,
  isBlobUploadTimeoutError,
  uploadDirectToBlob,
} from "../../utils/api";
import { AppTypes } from "../../enum/app";
import { isUnauthorized } from "../../utils/isUnauthorized";
import { trimInput, trimStart } from "../../utils/common";

const { TextArea } = Input;

const AdminAppEditPage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [form] = Form.useForm();

  // In a real app, load app details by id. For now, keep editable states local.
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoUrl, setLogoUrl] = useState<string>("");
  const [logoPreviewUrl, setLogoPreviewUrl] = useState<string>("");
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);

  // Fetch by id
  useEffect(() => {
    let isMounted = true;
    const fetchApp = async () => {
      try {
        const res = await api.get(`/api/admin/apps/${id}`);
        if (!isMounted) return;
        form.setFieldsValue({
          name: res.data.name,
          description: res.data.description,
          appInfo: res.data.appInfo,
          type: res.data.type,
        });
        setLogoUrl(res.data.logoUrl);
      } catch (e) {
        if (!isUnauthorized(e)) {
          message.error("アプリ情報の取得に失敗しました。");
        }
      } finally {
        if (isMounted) setLoading(false);
      }
    };
    fetchApp();
    return () => {
      isMounted = false;
    };
  }, [form, id]);

  // Generate/revoke preview URL when selecting a new logo file
  useEffect(() => {
    if (logoFile) {
      const url = URL.createObjectURL(logoFile);
      setLogoPreviewUrl(url);
      return () => URL.revokeObjectURL(url);
    } else {
      setLogoPreviewUrl("");
    }
  }, [logoFile]);

  const nameValue = Form.useWatch("name", form);
  const descValue = Form.useWatch("description", form);
  const appInfoValue = Form.useWatch("appInfo", form);

  const isValid = useMemo(() => {
    const hasBasics = nameValue && descValue && appInfoValue;
    const hasLogo = logoFile || logoUrl;
    return !!(hasBasics && hasLogo);
  }, [nameValue, descValue, appInfoValue, logoFile, logoUrl]);

  const onFinish = async (values: any) => {
    try {
      setSubmitting(true);
      // Upload logo on submit only if a new file is selected
      let finalLogoUrl = logoUrl;
      if (logoFile) {
        // derive folder prefix from app name
        const slug = String(values.name || "")
          .toLowerCase()
          .trim()
          .replace(/\s+/g, "-")
          .replace(/[^a-z0-9\-]/g, "");
        const basePrefix = slug
          ? `admin-apps/${slug}`
          : "admin-apps/unknown-app";
        const res = await uploadDirectToBlob(logoFile, `${basePrefix}/logo`);
        finalLogoUrl = res.url;
        setLogoUrl(finalLogoUrl);
      }
      await api.put(`/api/admin/apps/${id}`, {
        ...values,
        logoUrl: finalLogoUrl,
      });
      message.success("アプリを更新しました。");
      navigate("/admin");
    } catch (e) {
      if (!isUnauthorized(e)) {
        if (isBlobUploadTimeoutError(e)) {
          message.error(
            e.code === "UPLOAD_INACTIVITY_TIMEOUT"
              ? "アップロードが120秒間停止したため、タイムアウトしました。"
              : "アップロードが10分を超えたため、タイムアウトしました。",
          );
          return;
        }
        message.error("アプリ更新に失敗しました。");
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleRemove = () => {
    setLogoFile(null);
    setLogoUrl("");
  };

  return (
    <div className="flex w-full flex-col gap-5">
      <div>
        <h2 className="text-lg font-normal mb-2 text-start">
          アプリ情報の編集
        </h2>
        <Breadcrumb
          items={[
            { title: <Link to="/admin">アプリ一覧</Link> },
            { title: "アプリ情報の編集" },
          ]}
        />
      </div>

      <Form
        form={form}
        layout="vertical"
        requiredMark={false}
        onFinish={onFinish}
      >
        {/* 基本情報パネル */}
        <div className="bg-[#F4F6F9] rounded-2xl p-4">
          <div className="text-base font-normal mb-3 text-left">
            アプリの基本情報
          </div>
          {/* Row 1: 名前 + ロゴ */}
          <div className="grid grid-cols-1 gap-x-6 gap-y-3 md:grid-cols-2">
            <Form.Item
              label={
                <span className="font-medium">
                  名前 <span className="text-red-500">※</span>
                </span>
              }
              name="name"
              rules={[{ required: true, message: "" }]}
            >
              <Input
                maxLength={255}
                className="base-input"
                placeholder="アプリ名を入力"
                allowClear
                onChange={(e) => {
                  form.setFieldValue("name", trimStart(e.target.value));
                }}
                onBlur={(e) => {
                  form.setFieldValue("name", trimInput(e.target.value));
                }}
              />
            </Form.Item>
            <Form.Item
              label={
                <span className="font-medium">
                  ロゴ <span className="text-red-500">※</span>
                </span>
              }
              required={false}
            >
              <div className="flex items-start gap-3 upload-container">
                <Upload
                  beforeUpload={(file: File) => {
                    const isImage = file.type.startsWith("image/");
                    if (!isImage) {
                      message.error("画像ファイルのみアップロードできます。");
                      return Upload.LIST_IGNORE;
                    }
                    setLogoFile(file);
                    return false;
                  }}
                  maxCount={1}
                  showUploadList={logoFile ? { showPreviewIcon: false } : false}
                  accept="image/*"
                  onRemove={handleRemove}
                >
                  <Button
                    icon={<UploadOutlined />}
                    className="base-upload h-[46px] rounded-[6px] border px-[120px]"
                  >
                    アップロード
                  </Button>
                </Upload>
                {logoPreviewUrl ? (
                  <img
                    src={logoPreviewUrl}
                    alt="new logo preview"
                    className="h-[46px] w-[46x] p-1 object-contain rounded border border-[#ebeef2] bg-white"
                  />
                ) : (
                  logoUrl && (
                    <div className="flex items-center gap-2">
                      <img
                        src={logoUrl}
                        alt="current logo"
                        className="h-[46px] w-auto object-contain"
                      />
                    </div>
                  )
                )}
              </div>
            </Form.Item>

            <Form.Item
              label={
                <span className="font-medium">
                  説明 <span className="text-red-500">※</span>
                </span>
              }
              name="description"
              rules={[{ required: true, message: "" }]}
            >
              <Input
                maxLength={255}
                className="base-input"
                placeholder="アプリの説明を入力"
                allowClear
                onChange={(e) => {
                  const trimmed = e.target.value.trimStart();
                  form.setFieldValue("description", trimmed);
                }}
                onBlur={(e) => {
                  form.setFieldValue("description", trimInput(e.target.value));
                }}
              />
            </Form.Item>
            <Form.Item
              label={
                <span className="font-medium">
                  種類 <span className="text-red-500">※</span>
                </span>
              }
              name="type"
              rules={[{ required: true, message: "" }]}
            >
              <Select
                className="base-select h-[46px] text-start"
                disabled
                options={[
                  { value: AppTypes.APP, label: "アプリ" },
                  { value: AppTypes.PLUGIN, label: "プラグイン" },
                ]}
              />
            </Form.Item>
            <div className="col-span-2">
              <Form.Item
                label={
                  <span className="font-medium">
                    アプリ情報 <span className="text-red-500">※</span>
                  </span>
                }
                name="appInfo"
                rules={[{ required: true, message: "" }]}
              >
                <TextArea
                  className="base-input"
                  rows={6}
                  placeholder="アプリに関する詳細情報を入力"
                  onChange={(e) => {
                    const trimmed = e.target.value.trimStart();
                    form.setFieldValue("appInfo", trimmed);
                  }}
                  onBlur={(e) => {
                    form.setFieldValue("appInfo", trimInput(e.target.value));
                  }}
                  maxLength={1000}
                />
              </Form.Item>
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-[10px] mt-5">
          <Button
            className="base-button-outline h-[42px] w-[120px]"
            onClick={() => navigate("/admin")}
            disabled={submitting}
          >
            キャンセル
          </Button>
          <Button
            htmlType="submit"
            loading={submitting}
            disabled={!isValid}
            className="base-button h-[42px] w-[120px] tracking-[-1px]"
          >
            {submitting ? "保存中…" : "保存"}
          </Button>
        </div>
      </Form>
    </div>
  );
};

export default AdminAppEditPage;
